// The notification_settings row: kill switch, dry run, marketing daily cap.
// notification-worker re-reads it every poll, so a change applies within
// seconds. Turning real sending on asks for confirmation.
import React, {useEffect, useState} from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch,
  TextField, Typography
} from '@mui/material';

import {getSettings, updateSettings} from '../../actions/notifications';
import {errorMessage, formatTime} from '../../lib/notifications';

// Real email goes out only when sending is on AND dry run is off.
const sendsForReal = (s) => s && s.sendingEnabled && !s.dryRun;

const SettingsTab = () => {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    getSettings()
      .then((res) => { setSaved(res.settings); setForm(res.settings); })
      .catch((e) => setError(errorMessage(e)));
  }, []);

  const save = async () => {
    setConfirm(false);
    setBusy(true);
    setError('');
    try {
      const res = await updateSettings({...form, marketingDailyCap: Number(form.marketingDailyCap)});
      setSaved(res.settings);
      setForm(res.settings);
      setInfo('Saved. The worker picks it up within seconds.');
    } catch (e) {
      setError(errorMessage(e));
    }
    setBusy(false);
  };

  const onSave = () => (sendsForReal(form) && !sendsForReal(saved) ? setConfirm(true) : save());

  if (!form) return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={24} />;
  const capInvalid = !(Number(form.marketingDailyCap) >= 0);

  return (
    <Stack spacing={2} sx={{maxWidth: 520}}>
      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="success" onClose={() => setInfo('')}>{info}</Alert>}
      <Alert severity={sendsForReal(saved) ? 'success' : 'warning'}>
        {sendsForReal(saved)
          ? 'Emails are being sent.'
          : saved.sendingEnabled ? 'Dry run: the worker only logs what is due.' : 'Sending is switched off: queued emails wait.'}
      </Alert>
      <FormControlLabel control={<Switch checked={!!form.sendingEnabled}
        onChange={(e) => setForm({...form, sendingEnabled: e.target.checked})} />}
      label="Sending enabled (kill switch)" />
      <FormControlLabel control={<Switch checked={!!form.dryRun}
        onChange={(e) => setForm({...form, dryRun: e.target.checked})} />}
      label="Dry run (log only, send nothing)" />
      <TextField type="number" label="Marketing emails per day (cap)" value={form.marketingDailyCap}
        onChange={(e) => setForm({...form, marketingDailyCap: e.target.value})} error={capInvalid}
        helperText="Reminders and offers. Transactional email is never capped." inputProps={{min: 0}} />
      <Typography variant="caption" color="text.secondary">
        Last changed {formatTime(saved.updatedAt)}{saved.updatedBy ? ` by ${saved.updatedBy}` : ''}
      </Typography>
      <Button variant="contained" onClick={onSave} disabled={busy || capInvalid}>
        {busy ? <CircularProgress size={18} /> : 'Save'}
      </Button>
      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Start sending real emails?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Queued customer emails will be sent from now on (marketing ones up to the daily cap).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Start sending</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SettingsTab;
